import React from 'react';
import { UserLayout } from '../../components/user/UserLayout';
import { Card } from '../../components/ui/card';
import { useNavigate } from 'react-router-dom';
import { Brain, CheckCircle, BarChart3, Users, Layers, Building2, ArrowRight, DollarSign } from 'lucide-react';

export const SmartIntelligenceHome = () => {
  const navigate = useNavigate();

  const reports = [
    {
      id: 'proactive-checkins',
      title: 'Proactive Checkins',
      description: 'Track and analyze proactive checkin events by staff members',
      icon: CheckCircle,
      color: 'from-green-500 to-emerald-600',
      bgColor: 'bg-green-50',
      iconColor: 'text-green-600',
      path: '/user/smart-intelligence/proactive-checkins'
    },
    {
      id: 'overall-reports',
      title: 'Overall Reports',
      description: 'Comprehensive analytics and insights for your property',
      icon: BarChart3,
      color: 'from-blue-500 to-indigo-600',
      bgColor: 'bg-blue-50',
      iconColor: 'text-blue-600',
      path: '/user/smart-intelligence/reports'
    },
    {
      id: 'staff-comparison',
      title: 'Staff Comparison',
      description: 'Compare performance metrics between two staff members',
      icon: Users,
      color: 'from-purple-500 to-violet-600',
      bgColor: 'bg-purple-50',
      iconColor: 'text-purple-600',
      path: '/user/smart-intelligence/staff-comparison'
    },
    {
      id: 'section-comparison',
      title: 'Section Comparison',
      description: 'Compare performance metrics between two sections',
      icon: Layers,
      color: 'from-orange-500 to-amber-600',
      bgColor: 'bg-orange-50',
      iconColor: 'text-orange-600',
      path: '/user/smart-intelligence/section-comparison'
    },
    {
      id: 'venue-comparison',
      title: 'Venue Comparison',
      description: 'Compare performance metrics between two venues',
      icon: Building2,
      color: 'from-teal-500 to-cyan-600',
      bgColor: 'bg-teal-50',
      iconColor: 'text-teal-600',
      path: '/user/smart-intelligence/venue-comparison'
    },
    {
      id: 'revenue-report',
      title: 'Revenue Report',
      description: 'Track revenue by venue or section over any date range',
      icon: DollarSign,
      color: 'from-emerald-500 to-green-600',
      bgColor: 'bg-emerald-50',
      iconColor: 'text-emerald-600',
      path: '/user/smart-intelligence/revenue-report'
    }
  ];

  return (
    <UserLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center space-x-4">
          <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl flex items-center justify-center shadow-lg">
            <Brain className="w-8 h-8 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Smart Intelligence</h1>
            <p className="text-slate-600 mt-1">Analytics, reports, and data-driven insights</p>
          </div>
        </div>

        {/* Reports Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reports.map((report) => {
            const Icon = report.icon;
            return (
              <Card
                key={report.id}
                className="group cursor-pointer hover:shadow-xl transition-all duration-300 overflow-hidden border-0"
                onClick={() => navigate(report.path)}
                data-testid={`report-card-${report.id}`}
              >
                <div className={`h-2 bg-gradient-to-r ${report.color}`}></div>
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className={`w-12 h-12 ${report.bgColor} rounded-xl flex items-center justify-center`}>
                      <Icon className={`w-6 h-6 ${report.iconColor}`} />
                    </div>
                    <ArrowRight className="w-5 h-5 text-slate-300 group-hover:text-slate-500 group-hover:translate-x-1 transition-all" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-800 mb-2 group-hover:text-teal-600 transition-colors">
                    {report.title}
                  </h3>
                  <p className="text-sm text-slate-500 leading-relaxed">
                    {report.description}
                  </p>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Quick Stats Info */}
        <Card className="p-6 bg-gradient-to-r from-slate-50 to-slate-100 border-0">
          <div className="flex items-center space-x-4">
            <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
              <Brain className="w-5 h-5 text-teal-600" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-700">Smart Intelligence Tip</h3>
              <p className="text-sm text-slate-500">
                Use comparison reports to identify top performers and areas for improvement across staff, sections, and venues.
              </p>
            </div>
          </div>
        </Card>
      </div>
    </UserLayout>
  );
};
